import { useCallback, useEffect, useRef, useState } from "react";
import {
	Camera,
	Check,
	Loader2,
	RefreshCw,
	RotateCcw,
	Square,
	Video,
	X,
} from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

// Tirar foto e gravar vídeo pela câmera, sem sair da conversa.
//
// Antes, mandar uma foto do que está na bancada exigia fotografar no celular,
// passar para o computador e anexar — três passos para uma coisa que o
// atendente quer resolver em dois segundos. No celular era pior: o seletor de
// arquivos abre a galeria, não a câmera.
//
// A captura entra na mesma prévia dos anexos: dá para conferir, escrever
// legenda e mandar junto com outros arquivos.
const FORMATOS_DE_VIDEO = [
	{ mime: "video/webm;codecs=vp9,opus", ext: "webm" },
	{ mime: "video/webm;codecs=vp8,opus", ext: "webm" },
	{ mime: "video/webm", ext: "webm" },
	{ mime: "video/mp4", ext: "mp4" },
];

const escolherFormatoDeVideo = () => {
	if (typeof MediaRecorder === "undefined") return null;

	return (
		FORMATOS_DE_VIDEO.find((f) => MediaRecorder.isTypeSupported(f.mime)) || {
			mime: "",
			ext: "webm",
		}
	);
};

const doisDigitos = (n) => String(n).padStart(2, "0");

const CameraModal = ({ aberto, aoFechar, aoCapturar }) => {
	const videoRef = useRef(null);
	const streamRef = useRef(null);
	const gravadorRef = useRef(null);
	const pedacosRef = useRef([]);
	const cronometroRef = useRef(null);

	const [modo, setModo] = useState("foto");
	const [erro, setErro] = useState("");
	const [carregando, setCarregando] = useState(true);
	const [gravando, setGravando] = useState(false);
	const [segundos, setSegundos] = useState(0);
	const [frontal, setFrontal] = useState(false);
	const [temMaisDeUma, setTemMaisDeUma] = useState(false);
	// O que foi capturado e ainda não foi aceito: { arquivo, url, tipo }.
	const [previa, setPrevia] = useState(null);

	const pararStream = useCallback(() => {
		streamRef.current?.getTracks().forEach((t) => t.stop());
		streamRef.current = null;
	}, []);

	const abrirCamera = useCallback(async () => {
		setErro("");
		setCarregando(true);

		if (!navigator.mediaDevices?.getUserMedia) {
			// Sem HTTPS o navegador nem expõe a câmera — e o erro genérico
			// ("undefined") não diz a ninguém o que fazer.
			setErro(
				window.isSecureContext
					? "Este navegador não dá acesso à câmera."
					: "A câmera só funciona em HTTPS (ou em localhost).",
			);
			setCarregando(false);
			return;
		}

		try {
			pararStream();

			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: frontal ? "user" : "environment" },
				audio: true,
			});

			streamRef.current = stream;

			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play().catch(() => undefined);
			}

			const dispositivos = await navigator.mediaDevices.enumerateDevices();
			setTemMaisDeUma(
				dispositivos.filter((d) => d.kind === "videoinput").length > 1,
			);
		} catch (err) {
			setErro(
				err?.name === "NotAllowedError"
					? "Permissão de câmera negada no navegador."
					: "Não foi possível abrir a câmera.",
			);
		}

		setCarregando(false);
	}, [frontal, pararStream]);

	useEffect(() => {
		if (!aberto) return undefined;

		// Em promessa, e não direto: abrir a câmera muda estado (carregando,
		// erro) e chamar isso no corpo do efeito dispara render em cascata.
		Promise.resolve().then(abrirCamera);

		return () => {
			pararStream();
			if (cronometroRef.current) clearInterval(cronometroRef.current);
		};
	}, [aberto, abrirCamera, pararStream]);

	// A URL da prévia é revogada quando a captura muda: sem isso, cada foto
	// tirada e descartada fica ocupando memória até recarregar a página.
	useEffect(() => {
		return () => {
			if (previa?.url) window.URL.revokeObjectURL(previa.url);
		};
	}, [previa]);

	const fechar = () => {
		pararStream();
		setPrevia(null);
		setGravando(false);
		setSegundos(0);
		aoFechar();
	};

	const tirarFoto = () => {
		const video = videoRef.current;
		if (!video) return;

		const canvas = document.createElement("canvas");
		canvas.width = video.videoWidth || 1280;
		canvas.height = video.videoHeight || 720;

		const ctx = canvas.getContext("2d");
		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

		canvas.toBlob(
			(blob) => {
				if (!blob) return;

				const nome = `foto-${Date.now()}.jpg`;
				const arquivo = new File([blob], nome, { type: "image/jpeg" });

				setPrevia({
					arquivo,
					url: window.URL.createObjectURL(blob),
					tipo: "imagem",
				});
			},
			"image/jpeg",
			0.92,
		);
	};

	const comecarAGravar = () => {
		const stream = streamRef.current;
		if (!stream) return;

		const formato = escolherFormatoDeVideo();
		if (!formato) {
			setErro("Este navegador não grava vídeo.");
			return;
		}

		pedacosRef.current = [];

		const gravador = new MediaRecorder(
			stream,
			formato.mime ? { mimeType: formato.mime } : undefined,
		);

		gravador.ondataavailable = (e) => {
			if (e.data?.size) pedacosRef.current.push(e.data);
		};

		gravador.onstop = () => {
			const tipo = formato.mime?.split(";")[0] || "video/webm";
			const blob = new Blob(pedacosRef.current, { type: tipo });
			const arquivo = new File([blob], `video-${Date.now()}.${formato.ext}`, {
				type: tipo,
			});

			setPrevia({
				arquivo,
				url: window.URL.createObjectURL(blob),
				tipo: "video",
			});
		};

		gravador.start();
		gravadorRef.current = gravador;
		setGravando(true);
		setSegundos(0);

		cronometroRef.current = setInterval(
			() => setSegundos((s) => s + 1),
			1000,
		);
	};

	const pararDeGravar = () => {
		gravadorRef.current?.stop();
		gravadorRef.current = null;
		setGravando(false);
		if (cronometroRef.current) clearInterval(cronometroRef.current);
	};

	const usar = () => {
		if (!previa) return;

		aoCapturar(previa.arquivo);
		fechar();
	};

	const tempo = `${doisDigitos(Math.floor(segundos / 60))}:${doisDigitos(
		segundos % 60,
	)}`;

	return (
		<Dialog open={aberto} onOpenChange={(estado) => !estado && fechar()}>
			<DialogContent className="max-w-xl gap-3">
				<DialogTitle className="text-base">
					{previa
						? previa.tipo === "video"
							? "Vídeo gravado"
							: "Foto tirada"
						: modo === "video"
							? "Gravar vídeo"
							: "Tirar foto"}
				</DialogTitle>

				<div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg bg-black">
					{/* O vídeo fica montado sempre: recriar o elemento a cada
					    captura faz a câmera piscar e às vezes não volta. */}
					<video
						ref={videoRef}
						muted
						playsInline
						className={cn(
							"h-full w-full object-contain",
							previa && "hidden",
							frontal && "-scale-x-100",
						)}
					/>

					{previa?.tipo === "imagem" && (
						<img
							src={previa.url}
							alt="Foto capturada"
							className="h-full w-full object-contain"
						/>
					)}

					{previa?.tipo === "video" && (
						<video
							src={previa.url}
							controls
							playsInline
							className="h-full w-full object-contain"
						/>
					)}

					{!previa && carregando && (
						<span className="absolute flex items-center gap-2 text-sm text-white">
							<Loader2 className="h-4 w-4 animate-spin" />
							Abrindo a câmera…
						</span>
					)}

					{!previa && erro && (
						<div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
							<Camera className="h-8 w-8 text-white/70" />
							<p className="text-sm text-white">{erro}</p>
							<Button variant="secondary" size="sm" onClick={abrirCamera}>
								Tentar de novo
							</Button>
						</div>
					)}

					{gravando && (
						<span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/70 px-2 py-1 text-xs font-medium text-white">
							<span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
							{tempo}
						</span>
					)}
				</div>

				{previa ? (
					<div className="flex justify-end gap-2">
						<Button
							variant="ghost"
							className="gap-2"
							onClick={() => {
								setPrevia(null);
								abrirCamera();
							}}
						>
							<RotateCcw className="h-4 w-4" />
							Refazer
						</Button>
						<Button className="gap-2" onClick={usar}>
							<Check className="h-4 w-4" />
							Usar
						</Button>
					</div>
				) : (
					<div className="flex items-center justify-between gap-2">
						{/* Foto ou vídeo: a escolha muda o que o botão do meio faz. */}
						<div className="flex rounded-full border p-0.5">
							<button
								type="button"
								onClick={() => !gravando && setModo("foto")}
								className={cn(
									"flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
									modo === "foto"
										? "bg-primary text-primary-foreground"
										: "text-muted-foreground",
								)}
							>
								<Camera className="h-3.5 w-3.5" />
								Foto
							</button>
							<button
								type="button"
								onClick={() => !gravando && setModo("video")}
								className={cn(
									"flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
									modo === "video"
										? "bg-primary text-primary-foreground"
										: "text-muted-foreground",
								)}
							>
								<Video className="h-3.5 w-3.5" />
								Vídeo
							</button>
						</div>

						{modo === "foto" ? (
							<Button
								size="lg"
								className="gap-2 rounded-full"
								onClick={tirarFoto}
								disabled={Boolean(erro) || carregando}
							>
								<Camera className="h-5 w-5" />
								Tirar foto
							</Button>
						) : (
							<Button
								size="lg"
								variant={gravando ? "destructive" : "default"}
								className="gap-2 rounded-full"
								onClick={gravando ? pararDeGravar : comecarAGravar}
								disabled={Boolean(erro) || carregando}
							>
								{gravando ? (
									<>
										<Square className="h-4 w-4 fill-current" />
										Parar ({tempo})
									</>
								) : (
									<>
										<Video className="h-5 w-5" />
										Gravar
									</>
								)}
							</Button>
						)}

						<div className="flex items-center gap-1">
							{/* Frontal/traseira: no celular é o que decide entre
							    fotografar o documento ou a própria cara. */}
							{temMaisDeUma && (
								<Button
									variant="ghost"
									size="icon"
									title="Trocar de câmera"
									aria-label="Trocar de câmera"
									disabled={gravando}
									onClick={() => setFrontal((v) => !v)}
								>
									<RefreshCw className="h-4 w-4" />
								</Button>
							)}
							<Button
								variant="ghost"
								size="icon"
								title="Fechar"
								aria-label="Fechar"
								onClick={fechar}
							>
								<X className="h-4 w-4" />
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default CameraModal;
